import { router } from "@inertiajs/react"
import { Building2, Layers, Warehouse } from "lucide-react"
import { useState, type ComponentType } from "react"

import { Button } from "@/components/ui/button"
import { Field, Meter, Section, Tile, TileGrid } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { formatCurrency } from "@/lib/utils"
import { ProductionEntryHistory } from "@/components/production-entry-history"
import { RateBySection } from "@/pages/Projects/tabs/RateBySection"
import type { ProductionData } from "@/pages/Projects/tabs/types"

type SectionKey = "steel" | "wall" | "roof"

const SECTIONS: { key: SectionKey; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { key: "steel", label: "Steel erection", icon: Building2 },
  { key: "wall", label: "Wall panels", icon: Layers },
  { key: "roof", label: "Roof", icon: Warehouse },
]

const HOURS_KEY: Record<SectionKey, "steelHours" | "wallHours" | "roofHours"> = {
  steel: "steelHours",
  wall: "wallHours",
  roof: "roofHours",
}

function formatFinish(value?: string | null): string {
  if (!value) return "—"
  if (value === "unbounded") return "Unbounded"
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function daysUntil(value?: string | null): number | null {
  if (!value || value === "unbounded") return null
  return Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000)
}

function inputValue(value?: number | null): string {
  return value != null ? String(value) : ""
}

function entryFields(production: ProductionData, date: string) {
  const entry = production.entries.find((e) => e.date === date)
  return {
    steel: inputValue(entry?.steelSqftPlaced),
    wall: inputValue(entry?.wallSqftPlaced),
    roof: inputValue(entry?.roofSqftPlaced),
    steelHours: inputValue(entry?.steelHours), wallHours: inputValue(entry?.wallHours),
    roofHours: inputValue(entry?.roofHours),
  }
}

export function ProductionTab({
  endDate,
  production,
  projectId,
}: {
  endDate?: string | null
  production: ProductionData
  projectId: string
}) {
  const initialAsOf = production.productionAsOf ?? new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({ asOf: initialAsOf, ...entryFields(production, initialAsOf) })
  const sections = production.sections ?? []
  const initialOverrides = Object.fromEntries(sections.map((s) => [s.key, inputValue(s.rateOverride)]))
  const [overrides, setOverrides] = useState<Record<string, string>>(initialOverrides)

  const totals: Record<SectionKey, number> = {
    steel: production.steelSqftTotal ?? 0,
    wall: production.wallSqftTotal ?? 0,
    roof: production.roofSqftTotal ?? 0,
  }

  const placed: Record<SectionKey, number> = {
    steel: production.steelSqftCompleted ?? 0,
    wall: production.wallSqftCompleted ?? 0,
    roof: production.roofSqftCompleted ?? 0,
  }

  const totalSqft = totals.steel + totals.wall + totals.roof
  const doneSqft = placed.steel + placed.wall + placed.roof
  const overallPct = totalSqft > 0 ? (doneSqft / totalSqft) * 100 : 0

  const daysToFinish = daysUntil(production.finishExpected ?? production.estimatedFinish)
  const daysToEnd = daysUntil(endDate)
  const daysAhead = daysToEnd != null && daysToFinish != null ? daysToEnd - daysToFinish : null

  const saveOverride = (key: SectionKey) => {
    const data = { [`${key}RateOverride`]: overrides[key] ?? "" }
    router.patch(`/projects/${projectId}`, data, { preserveScroll: true })
  }

  const save = () => {
    if (!form.asOf) return
    router.post(
      `/projects/${projectId}/production_entries`,
      {
        entryDate: form.asOf,
        steelSqftPlaced: form.steel || null,
        wallSqftPlaced: form.wall || null,
        roofSqftPlaced: form.roof || null,
        steelHours: form.steelHours || null,
        wallHours: form.wallHours || null,
        roofHours: form.roofHours || null,
      },
      { preserveScroll: true },
    )
  }

  const schedule =
    daysAhead == null
      ? ""
      : daysAhead > 0
        ? ` · ${daysAhead} days ahead of the contract date`
        : daysAhead === 0
          ? " · on the contract date"
          : ` · ${Math.abs(daysAhead)} days behind the contract date`

  return (
    <Section className="pb-7">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-title font-semibold">Production</h2>
          <p className="mt-0.5 text-meta text-muted-foreground">
            {totalSqft > 0
              ? `${doneSqft.toLocaleString()} of ${totalSqft.toLocaleString()} sq ft placed · ${overallPct.toFixed(1)}% complete${schedule}`
              : "No square footage on the converted bid yet"}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <Field label="As of" className="w-[150px]">
            <Input
              type="date"
              value={form.asOf}
              onChange={(e) => setForm((f) => ({ ...f, asOf: e.target.value, ...entryFields(production, e.target.value) }))}
            />
          </Field>
          <Button size="sm" onClick={save}>
            Save &amp; recalculate
          </Button>
        </div>
      </div>

      <div className="mb-5">
        <TileGrid min={240}>
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const total = totals[key]
            const done = placed[key]
            const pct = total > 0 ? Math.min((done / total) * 100, 100) : 0

            return (
              <div key={key} className="bg-card px-4 py-3.5">
                <div className="mb-3 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-body font-medium">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    {label}
                  </span>
                  <span className="num text-label text-muted-foreground">
                    {total > 0 ? `${total.toLocaleString()} sq ft` : "—"}
                  </span>
                </div>
                <div className="flex items-end gap-2.5">
                  <Field label="Placed this entry" className="flex-1">
                    <Input
                      className="num"
                      type="number"
                      min="0"
                      value={form[key]}
                      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    />
                  </Field>
                  <span className="num text-[18px] font-semibold leading-relaxed tracking-[-0.02em]">
                    {Math.round(pct)}%
                  </span>
                </div>
                <div className="mt-1.5 text-label text-muted-foreground">
                  {done.toLocaleString()} sq ft placed to date
                </div>
                <div className="mt-2.5">
                  <Meter pct={pct} />
                </div>
                <div className="mt-2.5">
                  <Field label="Hours">
                    <Input
                      className="num"
                      type="number"
                      min="0"
                      value={form[HOURS_KEY[key]]}
                      onChange={(e) => setForm((f) => ({ ...f, [HOURS_KEY[key]]: e.target.value }))}
                    />
                  </Field>
                </div>
              </div>
            )
          })}
        </TileGrid>
      </div>

      <TileGrid>
        <Tile
          label="Production rate"
          value={production.measuredRate != null ? `${production.measuredRate.toLocaleString()} sq ft/hr` : "—"}
          sub="Placed per man-hour"
        />
        <Tile
          label="Hours logged"
          value={production.hoursLogged != null ? `${production.hoursLogged.toLocaleString()} hrs` : "—"}
          sub={`of ${(production.estimatedTotalHours ?? 0).toLocaleString()} estimated`}
        />
        <Tile
          label="Remaining hours"
          value={production.remainingHours != null ? `${production.remainingHours.toLocaleString()} hrs` : "—"}
          sub="At current productivity"
        />
        <Tile
          label="Weighted rate"
          value={
            production.weightedHourlyRate != null
              ? `${formatCurrency(production.weightedHourlyRate)}/hr`
              : "—"
          }
          sub="Blended from time entries"
        />
        <Tile
          label="Expected finish"
          value={formatFinish(production.finishExpected)}
          sub={`${formatFinish(production.finishEarly)} – ${formatFinish(production.finishLate)} range${production.confidenceBasis === "assumed" ? " (assumed)" : ""}`}
        />
        <Tile
          label="Remaining labor"
          value={production.remainingLaborCost != null ? formatCurrency(production.remainingLaborCost) : "—"}
          sub={
            production.dailyLaborCost != null ? `${formatCurrency(production.dailyLaborCost)} per day` : undefined
          }
        />
      </TileGrid>

      {sections.length > 0 ? (
        <RateBySection
          overrides={overrides}
          production={production}
          saveOverride={saveOverride}
          sections={sections}
          setOverrides={setOverrides}
        />
      ) : null}

      <div className="mt-6">
        <ProductionEntryHistory entries={production.entries} />
      </div>
    </Section>
  )
}
